import { MathUtils, Vector3 } from 'three';
import { HALF_CITY, PHYSICS, PLAYER } from '../config/settings';
import { Input } from '../core/Input';
import { Updatable } from '../core/types';
import { AABBCollider } from '../physics/AABBCollider';
import { Player } from './Player';
import { WebSystem } from './WebSystem';
import { MovementState } from './states/MovementState';
import { GroundedState } from './states/GroundedState';
import { AirborneState } from './states/AirborneState';
import { ClimbingState } from './states/ClimbingState';

/**
 * The player's brain. Each frame it: reads look input, builds the desired move
 * direction, selects the appropriate movement state, lets that state set the
 * velocity, integrates, applies web constraints, resolves collisions, and
 * handles the wall leap / roof mantle transitions.
 */
export class MovementController implements Updatable {
  private readonly grounded = new GroundedState();
  private readonly airborne = new AirborneState();
  private readonly climbing = new ClimbingState();
  private state: MovementState = this.airborne;

  private readonly forward = new Vector3();
  private readonly right = new Vector3();
  private readonly wish = new Vector3();
  private readonly intoWall = new Vector3();

  private climbStartListener: () => void = () => {};

  constructor(
    private readonly player: Player,
    private readonly input: Input,
    private readonly collider: AABBCollider,
    private readonly web: WebSystem,
  ) {}

  onClimbStart(listener: () => void): void {
    this.climbStartListener = listener;
  }

  get stateName(): string {
    return this.state.name;
  }

  update(dt: number): void {
    this.applyLook();
    this.buildWish();
    const moving = this.wish.lengthSq() > 0;

    if (this.player.climbRelockTimer > 0) this.player.climbRelockTimer -= dt;
    if (this.state === this.climbing && this.input.isDown('Space')) this.leapOffWall();

    this.transitionTo(this.selectState(moving));
    this.state.update({ dt, wish: this.wish, moving, input: this.input, player: this.player });

    this.player.position.addScaledVector(this.player.velocity, dt);
    this.web.applyConstraints(dt);
    this.collider.resolve(this.player);

    this.tryMantle();
    this.clampToWorld();
  }

  private selectState(moving: boolean): MovementState {
    if (this.web.isSwinging) return this.airborne;
    const canCling = this.player.climbRelockTimer <= 0 && this.player.touchingWall;
    if (canCling && (!this.player.grounded || (moving && this.pressingIntoWall()))) return this.climbing;
    if (this.player.grounded) return this.grounded;
    return this.airborne;
  }

  private transitionTo(next: MovementState): void {
    if (next === this.state) return;
    if (next === this.climbing) this.climbStartListener();
    this.state = next;
  }

  private pressingIntoWall(): boolean {
    this.intoWall.copy(this.player.wallNormal).multiplyScalar(-1);
    return this.wish.dot(this.intoWall) > 0.2;
  }

  private leapOffWall(): void {
    this.player.velocity.copy(this.player.wallNormal).multiplyScalar(PHYSICS.WALL_LEAP_OUT);
    this.player.velocity.y = PHYSICS.WALL_LEAP_UP;
    this.player.climbRelockTimer = PHYSICS.CLIMB_RELOCK_DELAY;
  }

  /** Vault over the parapet onto the roof when climbing near the top. */
  private tryMantle(): void {
    if (this.state !== this.climbing) return;
    if (!this.input.isDown('KeyW')) return;
    if (this.player.position.y < this.player.wallTopY - PHYSICS.MANTLE_REACH) return;

    this.player.position.addScaledVector(this.player.wallNormal, -(this.player.radius + 0.7));
    this.player.position.y = this.player.wallTopY + 0.02;
    this.player.velocity.set(0, 0, 0);
    this.player.grounded = true;
    this.state = this.grounded;
  }

  private applyLook(): void {
    const { x, y } = this.input.consumeMouseDelta();
    this.player.yaw -= x * PLAYER.LOOK_SENSITIVITY;
    this.player.pitch -= y * PLAYER.LOOK_SENSITIVITY;
    this.player.pitch = MathUtils.clamp(this.player.pitch, -1.45, 1.45);
  }

  private buildWish(): void {
    this.forward.set(-Math.sin(this.player.yaw), 0, -Math.cos(this.player.yaw));
    this.right.set(-this.forward.z, 0, this.forward.x);

    this.wish.set(0, 0, 0);
    if (this.input.isDown('KeyW')) this.wish.add(this.forward);
    if (this.input.isDown('KeyS')) this.wish.sub(this.forward);
    if (this.input.isDown('KeyD')) this.wish.add(this.right);
    if (this.input.isDown('KeyA')) this.wish.sub(this.right);
    if (this.wish.lengthSq() > 0) this.wish.normalize();
  }

  private clampToWorld(): void {
    const limit = HALF_CITY + 160;
    this.player.position.x = MathUtils.clamp(this.player.position.x, -limit, limit);
    this.player.position.z = MathUtils.clamp(this.player.position.z, -limit, limit);
  }
}
